import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { CHART_INK, tone, useIsDarkMode } from '@/lib/chartTheme';
import { formatCompactMoney } from '@/lib/money';
import type { FinanceChannelFieldAvailability } from '@/api/generated/model';
import { financeColor } from '../lib/financeColors';
import { FinanceChartFilters, type FinanceChartFilterProps } from './FinanceChartFilters';
import { FinanceRefreshing, NotReadyBadge, NotReadyInline, NotReadyNote } from './FinanceNotReady';

export type OpExSeriesView = {
  name: string;
  category?: string;
  values: number[];
  total: number;
};

export type IncentiveBreakdownView = {
  referralRewards: number | null;
  referralAvailability: FinanceChannelFieldAvailability;
  creatorBonusesAvailability: FinanceChannelFieldAvailability;
  campaignRewardsAvailability: FinanceChannelFieldAvailability;
  note?: string;
};

const SHELL_LEGEND = [
  { key: 'APPLE', label: 'Apple IAP fees' },
  { key: 'BANK', label: 'Bank fees' },
] as const;

/** Confluence §12 Operating Expense Trends — no GST. Apple/Bank stay shells. */
export function OperatingExpenseTrendsCard({
  periods,
  series,
  currency,
  incentives,
  range,
  onRangeChange,
  countryCode,
  onCountryCodeChange,
  availableCountryCodes = [],
  startDate,
  endDate,
  isLoading = false,
}: {
  periods: string[];
  series: OpExSeriesView[];
  currency: string;
  incentives?: IncentiveBreakdownView;
} & FinanceChartFilterProps) {
  const isDark = useIsDarkMode();
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const visibleSeries = series.filter((item) => !hidden.has(item.name));
  const compactMoney = (value: number | string) => formatCompactMoney(Number(value), currency);
  const axisMoney = (value: number | string) =>
    formatCompactMoney(Number(value), currency, { withCode: false });

  const hasLiveData = series.some((item) => item.total > 0 || item.values.some((v) => v > 0));

  const option = useMemo(() => {
    const ink = {
      primary: tone(CHART_INK.primary, isDark),
      muted: tone(CHART_INK.muted, isDark),
      grid: tone(CHART_INK.grid, isDark),
      axis: tone(CHART_INK.axis, isDark),
      surface: tone(CHART_INK.surface, isDark),
    };

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: ink.surface,
        borderColor: ink.grid,
        textStyle: { color: ink.primary },
        valueFormatter: compactMoney,
      },
      grid: { left: 6, right: 10, top: 12, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: periods,
        axisLine: { lineStyle: { color: ink.axis } },
        axisTick: { show: false },
        axisLabel: { color: ink.muted, fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        min: 0,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: ink.muted, fontSize: 10, formatter: axisMoney },
        splitLine: { lineStyle: { color: ink.grid, type: 'dashed' } },
      },
      series: visibleSeries.map((item) => {
        const color = financeColor(item.category ?? item.name);
        return {
          name: item.name,
          type: 'line',
          smooth: 0.2,
          showSymbol: false,
          lineStyle: { width: 2, color },
          itemStyle: { color },
          emphasis: { focus: 'series' },
          data: item.values,
        };
      }),
    };
  }, [currency, isDark, periods, visibleSeries]);

  function setSeriesVisible(name: string, visible: boolean) {
    setHidden((current) => {
      const next = new Set(current);
      if (visible) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  return (
    <Card className="shadow-none">
      <CardHeader className="space-y-1 p-5 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-sm font-medium text-sky-400">
                Operating Expense Trends
              </CardTitle>
              <NotReadyBadge
                state={hasLiveData ? 'partial' : 'not-ready'}
                reason={
                  hasLiveData
                    ? 'Partial — Apple IAP commission and bank fees are not ready. Stripe fees and referral incentives appear when writers populate those tables.'
                    : 'Not ready — Stripe fees and referral incentives appear when writers populate fee / referral tables. Apple commission and bank fees stay shells.'
                }
              />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Apple · Stripe · Bank · Creator Incentives. GST excluded (tax ≠ OpEx).
            </p>
          </div>
          <FinanceChartFilters
            range={range}
            onRangeChange={onRangeChange}
            countryCode={countryCode}
            onCountryCodeChange={onCountryCodeChange}
            availableCountryCodes={availableCountryCodes}
            {...(startDate ? { startDate } : {})}
            {...(endDate ? { endDate } : {})}
            dateAriaLabel="Change Operating Expense Trends date range"
            countryAriaLabel="Change Operating Expense Trends creator country"
          />
        </div>
      </CardHeader>
      <CardContent className="p-5 pt-2">
        <FinanceRefreshing isLoading={isLoading}>
          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_210px]">
        {!hasLiveData ? (
          <div className="flex h-48 items-center justify-center rounded-md border border-dashed border-border/70 bg-muted/20 px-4 text-center text-xs text-muted-foreground">
            Stripe fees and referral incentives appear when writers populate fee / referral tables.
            Apple commission and bank fees stay shells.
          </div>
        ) : (
          <ReactECharts option={option} notMerge style={{ height: 240, width: '100%' }} />
        )}
        <div className="space-y-4">
          <div className="grid gap-2">
            {series.map((item) => {
              const visible = !hidden.has(item.name);
              const color = financeColor(item.category ?? item.name);
              return (
                <label key={item.name} className="flex cursor-pointer items-center gap-2 text-xs">
                  <Checkbox
                    checked={visible}
                    onCheckedChange={(checked) => setSeriesVisible(item.name, checked === true)}
                    style={{
                      borderColor: color,
                      backgroundColor: visible ? color : 'transparent',
                    }}
                    aria-label={`Show ${item.name}`}
                  />
                  <span className="min-w-0 flex-1 truncate">{item.name}</span>
                  <span className="text-muted-foreground">
                    {formatCompactMoney(item.total, currency)}
                  </span>
                </label>
              );
            })}
            {SHELL_LEGEND.map((item) => (
              <div key={item.key} className="flex items-center justify-between gap-2 text-xs">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span
                    className="size-2.5 rounded-full bg-muted-foreground/40"
                    aria-hidden
                  />
                  {item.label}
                  <NotReadyInline
                    className="ml-0"
                    reason={
                      item.key === 'APPLE'
                        ? 'Apple IAP commission is not stored on payments yet.'
                        : 'Bank fees are not modeled yet.'
                    }
                  />
                </span>
                <span className="text-muted-foreground">—</span>
              </div>
            ))}
          </div>

          {incentives ? (
            <div className="rounded-md border border-border/50 bg-muted/10 px-3 py-2.5">
              <p className="mb-2 text-[11px] font-medium text-foreground">Creator Incentives</p>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Referral</span>
                  <span className="tabular-nums">
                    {incentives.referralAvailability === 'AVAILABLE' &&
                    incentives.referralRewards != null
                      ? formatCompactMoney(incentives.referralRewards, currency)
                      : '—'}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">
                    Bonuses
                    <NotReadyInline reason="Creator bonuses await a dedicated table." />
                  </span>
                  <span className="text-muted-foreground">—</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">
                    Campaigns
                    <NotReadyInline reason="Campaign rewards await a dedicated table." />
                  </span>
                  <span className="text-muted-foreground">—</span>
                </div>
              </div>
              {incentives.note ? (
                <p className="mt-2 text-[10px] leading-4 text-muted-foreground">{incentives.note}</p>
              ) : (
                <NotReadyNote className="mt-2">
                  Bonuses and Campaigns are not ready yet. Referral uses completed referral fees when
                  present.
                </NotReadyNote>
              )}
            </div>
          ) : null}

          <NotReadyNote>
            Apple IAP commission and bank fees are not ready. Stripe provider fees and referral
            incentives appear when fee / referral writers populate those tables.
          </NotReadyNote>
        </div>
          </div>
        </FinanceRefreshing>
      </CardContent>
    </Card>
  );
}
